import React from 'react'

export type DashboardV2StepPhase = 'done' | 'current' | 'upcoming'

type DashboardV2ProgressStepperProps = {
  request: DashboardV2StepPhase
  /** Omit for flows with no payment step (e.g. redemptions): Request → Executed */
  payment?: DashboardV2StepPhase
  executed: DashboardV2StepPhase
  labels?: [string, string, string]
}

const IconStepDone = () => (
  <svg className='dashboard-v2__withdrawal-stepper__icon-svg' viewBox='0 0 24 24' aria-hidden>
    <circle cx='12' cy='12' r='11' fill='currentColor' />
    <path
      d='M8 12.5l2.5 2.5L16 9.5'
      fill='none'
      stroke='#fff'
      strokeWidth='2'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
  </svg>
)

const IconStepHourglass = () => (
  <svg className='dashboard-v2__withdrawal-stepper__icon-svg' viewBox='0 0 24 24' aria-hidden>
    <circle cx='12' cy='12' r='11' fill='none' stroke='currentColor' strokeWidth='1.5' />
    <path
      d='M9 7h6M9 17h6M9 7c0 2 1.5 3 3 4 1.5-1 3-2 3-4M9 17c0-2 1.5-3 3-4 1.5 1 3 2 3 4'
      fill='none'
      stroke='currentColor'
      strokeWidth='1.35'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
  </svg>
)

const IconStepPending = () => (
  <svg className='dashboard-v2__withdrawal-stepper__icon-svg' viewBox='0 0 24 24' aria-hidden>
    <circle cx='12' cy='12' r='11' fill='none' stroke='currentColor' strokeWidth='1.5' />
  </svg>
)

const IconStepPadlock = () => (
  <svg className='dashboard-v2__withdrawal-stepper__icon-svg' viewBox='0 0 24 24' aria-hidden>
    <rect
      x='7'
      y='10'
      width='10'
      height='9'
      rx='1.5'
      fill='none'
      stroke='currentColor'
      strokeWidth='1.4'
    />
    <path
      d='M8.5 10V8a3.5 3.5 0 0 1 7 0v2'
      fill='none'
      stroke='currentColor'
      strokeWidth='1.4'
      strokeLinecap='round'
    />
    <circle cx='12' cy='14.5' r='1.2' fill='currentColor' />
  </svg>
)

const StepIcon = ({
  phase,
  variant,
}: {
  phase: DashboardV2StepPhase
  variant: 'request' | 'payment' | 'executed'
}) => {
  if (phase === 'done') {
    return (
      <span className='dashboard-v2__withdrawal-stepper__icon dashboard-v2__withdrawal-stepper__icon--done'>
        <IconStepDone />
      </span>
    )
  }
  if (phase === 'current') {
    return (
      <span className='dashboard-v2__withdrawal-stepper__icon dashboard-v2__withdrawal-stepper__icon--current'>
        <IconStepHourglass />
      </span>
    )
  }
  if (variant === 'executed') {
    return (
      <span className='dashboard-v2__withdrawal-stepper__icon dashboard-v2__withdrawal-stepper__icon--upcoming'>
        <IconStepPadlock />
      </span>
    )
  }
  return (
    <span className='dashboard-v2__withdrawal-stepper__icon dashboard-v2__withdrawal-stepper__icon--upcoming'>
      <IconStepPending />
    </span>
  )
}

export const DashboardV2ProgressStepper: React.FC<DashboardV2ProgressStepperProps> = ({
  request,
  payment,
  executed,
  labels = ['Request', 'Payment', 'Executed'],
}) => {
  const line1Active = request === 'done'
  const hasPayment = payment !== undefined
  const line2Active = payment === 'done'

  const labelClass = (phase: DashboardV2StepPhase) =>
    phase === 'upcoming'
      ? 'dashboard-v2__withdrawal-stepper__label dashboard-v2__withdrawal-stepper__label--muted'
      : 'dashboard-v2__withdrawal-stepper__label'

  return (
    <div className='dashboard-v2__withdrawal-stepper' aria-hidden>
      <div className='dashboard-v2__withdrawal-stepper__track'>
        <div className='dashboard-v2__withdrawal-stepper__step'>
          <StepIcon phase={request} variant='request' />
          <span className={labelClass(request)}>{labels[0]}</span>
        </div>
        <div
          className={
            line1Active
              ? 'dashboard-v2__withdrawal-stepper__line dashboard-v2__withdrawal-stepper__line--active'
              : 'dashboard-v2__withdrawal-stepper__line'
          }
        />
        {hasPayment ? (
          <>
            <div className='dashboard-v2__withdrawal-stepper__step'>
              <StepIcon phase={payment} variant='payment' />
              <span className={labelClass(payment)}>{labels[1]}</span>
            </div>
            <div
              className={
                line2Active
                  ? 'dashboard-v2__withdrawal-stepper__line dashboard-v2__withdrawal-stepper__line--active'
                  : 'dashboard-v2__withdrawal-stepper__line'
              }
            />
          </>
        ) : null}
        <div className='dashboard-v2__withdrawal-stepper__step'>
          <StepIcon phase={executed} variant='executed' />
          <span className={labelClass(executed)}>{labels[2]}</span>
        </div>
      </div>
    </div>
  )
}
